import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  alpha,
  Container,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import {
  LocalOffer as TagIcon,
  DirectionsCar as LicensePlateIcon,
  PersonAdd as AssignIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Home as HomeIcon,
  QrCode as QrCodeIcon,
  Nfc as NfcIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TaggingIdentifier = () => {
  const { user } = useAuth();
  
  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  // State management
  const [detectionMethod, setDetectionMethod] = useState('license_plate'); // 'license_plate' or 'number_tags'
  const [assignedTags, setAssignedTags] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState('');
  const [tagNumber, setTagNumber] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Mock data for now (will be replaced with real API calls)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Mock assigned tags data
        setAssignedTags([
          {
            id: 1,
            tagNumber: 'TAG001',
            parentName: 'John Smith',
            parentEmail: 'john.smith@email.com',
            assignedDate: '2024-01-15',
            status: 'active',
            studentCount: 2
          },
          {
            id: 2,
            tagNumber: 'TAG002',
            parentName: 'Sarah Johnson',
            parentEmail: 'sarah.j@email.com',
            assignedDate: '2024-01-16',
            status: 'active',
            studentCount: 1
          },
          {
            id: 3,
            tagNumber: 'TAG003',
            parentName: 'Mike Davis',
            parentEmail: 'mike.davis@email.com',
            assignedDate: '2024-01-10',
            status: 'inactive',
            studentCount: 3
          }
        ]);

        // Mock parents data
        setParents([
          { id: 1, name: 'John Smith', email: 'john.smith@email.com' },
          { id: 2, name: 'Sarah Johnson', email: 'sarah.j@email.com' },
          { id: 3, name: 'Emily Brown', email: 'emily.brown@email.com' },
          { id: 4, name: 'David Wilson', email: 'david.wilson@email.com' }
        ]);

      } catch (error) {
        console.error('Error loading tagging data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading data. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleDetectionMethodChange = (event) => {
    setDetectionMethod(event.target.checked ? 'number_tags' : 'license_plate');
  };

  const handleAssignTag = async () => {
    try {
      if (!selectedParent || !tagNumber) {
        setSnackbar({
          open: true,
          message: 'Please select a parent and enter a tag number.',
          severity: 'warning'
        });
        return;
      }

      const parentData = parents.find(p => p.id === selectedParent);
      const newTag = {
        id: Date.now(),
        tagNumber: tagNumber.toUpperCase(),
        parentName: parentData.name,
        parentEmail: parentData.email,
        assignedDate: new Date().toISOString().split('T')[0],
        status: 'active',
        studentCount: 1
      };

      setAssignedTags(prev => [...prev, newTag]);
      setAssignDialogOpen(false);
      setSelectedParent('');
      setTagNumber('');
      
      setSnackbar({
        open: true,
        message: `Tag ${newTag.tagNumber} assigned successfully!`,
        severity: 'success'
      });

    } catch (error) {
      console.error('Error assigning tag:', error);
      setSnackbar({
        open: true,
        message: 'Error assigning tag. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleRemoveTag = (tagId) => {
    setAssignedTags(prev => prev.filter(tag => tag.id !== tagId));
    setSnackbar({
      open: true,
      message: 'Tag removed successfully!',
      severity: 'info'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return {
          backgroundColor: safeLineColors.lightEmerald,
          color: safeLineColors.forestGreen,
          borderColor: safeLineColors.emerald
        };
      case 'inactive':
        return {
          backgroundColor: safeLineColors.lightRed,
          color: safeLineColors.darkRed,
          borderColor: safeLineColors.red
        };
      default:
        return {
          backgroundColor: safeLineColors.mediumGray,
          color: safeLineColors.darkCharcoal,
          borderColor: safeLineColors.coolGray
        };
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="h6" sx={{ color: safeLineColors.mediumCharcoal }}>
          Loading tagging system...
        </Typography>
        <Box sx={{ 
          width: 40,
          height: 40,
          border: `3px solid ${safeLineColors.coolGray}`,
          borderTop: `3px solid ${safeLineColors.flameOrange}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' }
          }
        }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: safeLineColors.lightGray, 
      minHeight: '100vh',
      pb: 4
    }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          aria-label="breadcrumb" 
          sx={{ 
            mb: 3,
            '& .MuiBreadcrumbs-separator': {
              color: safeLineColors.mutedGray
            }
          }}
        >
          <MuiLink 
            component={RouterLink}
            to="/dashboard"
            underline="hover" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: safeLineColors.mediumCharcoal,
              cursor: 'pointer',
              '&:hover': {
                color: safeLineColors.flameOrange
              }
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Dashboard
          </MuiLink>
          <Typography 
            color="text.primary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: safeLineColors.flameOrange,
              fontWeight: 'medium'
            }}
          >
            <TagIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Tagging Identifier
          </Typography>
        </Breadcrumbs>

        {/* Enhanced Header */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${safeLineColors.pureWhite} 0%, ${safeLineColors.orangeTint} 100%)`,
            border: `1px solid ${safeLineColors.coolGray}`,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
            }
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                  mr: 3,
                  boxShadow: `0 8px 16px ${alpha(safeLineColors.flameOrange, 0.3)}`
                }}>
                  <TagIcon sx={{ color: safeLineColors.pureWhite, fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 700,
                      color: safeLineColors.darkCharcoal,
                      mb: 1,
                      background: `linear-gradient(135deg, ${safeLineColors.darkCharcoal}, ${safeLineColors.mediumCharcoal})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Tagging Identifier System
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: safeLineColors.mediumCharcoal,
                      fontWeight: 400,
                      mb: 2
                    }}
                  >
                    Choose identification method and manage parent tags
                  </Typography>
                  
                  {/* Current Method Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
                      Current Method:
                    </Typography>
                    <Chip 
                      icon={detectionMethod === 'license_plate' ? <LicensePlateIcon /> : <TagIcon />}
                      label={detectionMethod === 'license_plate' ? 'License Plate Detection' : 'Custom Number Tags'}
                      sx={{
                        backgroundColor: safeLineColors.lightEmerald,
                        color: safeLineColors.forestGreen,
                        fontWeight: 'medium'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AssignIcon />}
                  onClick={() => setAssignDialogOpen(true)}
                  disabled={detectionMethod === 'license_plate'}
                  sx={{
                    background: detectionMethod === 'license_plate' 
                      ? safeLineColors.mutedGray 
                      : `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    '&:hover': {
                      background: detectionMethod === 'license_plate' 
                        ? safeLineColors.mutedGray 
                        : `linear-gradient(135deg, ${safeLineColors.brightOrange}, ${safeLineColors.flameOrange})`
                    }
                  }}
                >
                  Assign New Tag
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {/* Detection Method Configuration */}
          <Grid item xs={12} lg={5}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                backgroundColor: safeLineColors.pureWhite,
                border: `1px solid ${safeLineColors.coolGray}`,
                height: 'fit-content',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
                }
              }}
            >
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2,
                      backgroundColor: safeLineColors.flameOrange,
                      mr: 2
                    }}>
                      <QrCodeIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        color: safeLineColors.darkCharcoal 
                      }}>
                        Detection Method
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: safeLineColors.mediumCharcoal 
                      }}>
                        Choose your identification system
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ 
                  backgroundColor: safeLineColors.orangeTint,
                  borderBottom: `1px solid ${safeLineColors.coolGray}`,
                  pb: 2,
                  pt: 2
                }}
              />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={detectionMethod === 'number_tags'}
                        onChange={handleDetectionMethodChange}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: safeLineColors.flameOrange,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: safeLineColors.flameOrange,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ 
                        color: safeLineColors.darkCharcoal, 
                        fontWeight: 'medium',
                        fontSize: '1.1rem'
                      }}>
                        Use Custom Number Tags
                      </Typography>
                    }
                  />
                </Box>

                {/* License Plate Detection Option */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    border: `2px solid ${detectionMethod === 'license_plate' ? safeLineColors.flameOrange : safeLineColors.coolGray}`,
                    borderRadius: 2,
                    backgroundColor: detectionMethod === 'license_plate' ? safeLineColors.orangeTint : safeLineColors.lightGray,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LicensePlateIcon sx={{ 
                      color: detectionMethod === 'license_plate' ? safeLineColors.flameOrange : safeLineColors.mutedGray,
                      fontSize: 32,
                      mr: 2
                    }} />
                    <Box>
                      <Typography variant="h6" sx={{ 
                        color: safeLineColors.darkCharcoal,
                        fontWeight: 'bold'
                      }}>
                        License Plate Detection
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: safeLineColors.mediumCharcoal
                      }}>
                        Automatic recognition using camera system
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: safeLineColors.mediumCharcoal,
                    lineHeight: 1.6
                  }}>
                    Parents use their vehicle license plates for identification. The system automatically detects and verifies license plates registered to approved guardians.
                  </Typography>
                </Paper>

                {/* Custom Number Tags Option */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: `2px solid ${detectionMethod === 'number_tags' ? safeLineColors.flameOrange : safeLineColors.coolGray}`,
                    borderRadius: 2,
                    backgroundColor: detectionMethod === 'number_tags' ? safeLineColors.orangeTint : safeLineColors.lightGray,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TagIcon sx={{ 
                      color: detectionMethod === 'number_tags' ? safeLineColors.flameOrange : safeLineColors.mutedGray,
                      fontSize: 32,
                      mr: 2
                    }} />
                    <Box>
                      <Typography variant="h6" sx={{ 
                        color: safeLineColors.darkCharcoal,
                        fontWeight: 'bold'
                      }}>
                        Custom Number Tags
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: safeLineColors.mediumCharcoal
                      }}>
                        Physical tags with unique numbers
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: safeLineColors.mediumCharcoal,
                    lineHeight: 1.6
                  }}>
                    School provides parents with physical tags containing unique numbers. Parents present these tags for student pickup identification.
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          {/* Assigned Tags Management */}
          <Grid item xs={12} lg={7}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                backgroundColor: safeLineColors.pureWhite,
                border: `1px solid ${safeLineColors.coolGray}`,
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
                }
              }}
            >
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2,
                        backgroundColor: safeLineColors.flameOrange,
                        mr: 2
                      }}>
                        <NfcIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 'bold', 
                          color: safeLineColors.darkCharcoal 
                        }}>
                          Assigned Tags
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: safeLineColors.mediumCharcoal 
                        }}>
                          {detectionMethod === 'license_plate' 
                            ? 'License plates are managed automatically' 
                            : `${assignedTags.length} tags assigned`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                }
                sx={{ 
                  backgroundColor: safeLineColors.orangeTint,
                  borderBottom: `1px solid ${safeLineColors.coolGray}`,
                  pb: 2,
                  pt: 2
                }}
              />
              
              {detectionMethod === 'license_plate' ? (
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <LicensePlateIcon sx={{ 
                    fontSize: 64, 
                    color: safeLineColors.mutedGray,
                    mb: 2
                  }} />
                  <Typography variant="h6" sx={{ 
                    color: safeLineColors.mediumCharcoal,
                    fontWeight: 'medium',
                    mb: 1
                  }}>
                    License Plate Mode Active
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: safeLineColors.mutedGray,
                    maxWidth: 400,
                    mx: 'auto'
                  }}>
                    License plates are automatically detected and managed through the parent registration system. No manual tag assignment needed.
                  </Typography>
                </CardContent>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead sx={{ backgroundColor: safeLineColors.lightGray }}>
                      <TableRow>
                        <TableCell sx={{ 
                          fontWeight: 'bold', 
                          color: safeLineColors.darkCharcoal,
                          borderBottom: `2px solid ${safeLineColors.coolGray}`
                        }}>
                          Parent
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 'bold', 
                          color: safeLineColors.darkCharcoal,
                          borderBottom: `2px solid ${safeLineColors.coolGray}`
                        }}>
                          Tag Number
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 'bold', 
                          color: safeLineColors.darkCharcoal,
                          borderBottom: `2px solid ${safeLineColors.coolGray}`
                        }}>
                          Assigned Date
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 'bold', 
                          color: safeLineColors.darkCharcoal,
                          borderBottom: `2px solid ${safeLineColors.coolGray}`
                        }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 'bold', 
                          color: safeLineColors.darkCharcoal,
                          borderBottom: `2px solid ${safeLineColors.coolGray}`
                        }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignedTags.map((tag) => (
                        <TableRow 
                          key={tag.id}
                          hover
                          sx={{
                            '&:hover': {
                              backgroundColor: safeLineColors.orangeTint
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ 
                                bgcolor: safeLineColors.flameOrange,
                                mr: 2,
                                width: 36,
                                height: 36,
                                fontSize: '0.9rem',
                                fontWeight: 'bold'
                              }}>
                                {getInitials(tag.parentName)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" sx={{ 
                                  fontWeight: 'medium',
                                  color: safeLineColors.darkCharcoal
                                }}>
                                  {tag.parentName}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                  color: safeLineColors.mediumCharcoal
                                }}>
                                  {tag.parentEmail}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tag.tagNumber}
                              size="small"
                              sx={{
                                backgroundColor: safeLineColors.flameOrange,
                                color: safeLineColors.pureWhite,
                                fontWeight: 'bold',
                                fontFamily: 'monospace'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              color: safeLineColors.mediumCharcoal
                            }}>
                              {new Date(tag.assignedDate).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={tag.status === 'active' ? <CheckCircleIcon /> : <CancelIcon />}
                              label={tag.status.charAt(0).toUpperCase() + tag.status.slice(1)} 
                              size="small"
                              sx={{
                                ...getStatusColor(tag.status),
                                border: `1px solid ${getStatusColor(tag.status).borderColor}`,
                                fontWeight: 'medium'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Edit Tag">
                                <IconButton 
                                  size="small"
                                  sx={{ 
                                    color: safeLineColors.flameOrange,
                                    '&:hover': {
                                      backgroundColor: safeLineColors.orangeTint
                                    }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove Tag">
                                <IconButton 
                                  size="small"
                                  onClick={() => handleRemoveTag(tag.id)}
                                  sx={{ 
                                    color: safeLineColors.red,
                                    '&:hover': {
                                      backgroundColor: safeLineColors.lightRed
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {assignedTags.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                            <TagIcon sx={{ 
                              fontSize: 48, 
                              color: safeLineColors.mutedGray,
                              mb: 2
                            }} />
                            <Typography variant="h6" sx={{ 
                              color: safeLineColors.mediumCharcoal,
                              fontWeight: 'medium',
                              mb: 1
                            }}>
                              No tags assigned yet
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: safeLineColors.mutedGray
                            }}>
                              Click "Assign New Tag" to get started
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* Assign Tag Dialog */}
        <Dialog 
          open={assignDialogOpen} 
          onClose={() => setAssignDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${safeLineColors.coolGray}`
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: safeLineColors.orangeTint,
            borderBottom: `1px solid ${safeLineColors.coolGray}`,
            color: safeLineColors.darkCharcoal,
            fontWeight: 'bold'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TagIcon sx={{ mr: 1, color: safeLineColors.flameOrange }} />
              Assign New Tag
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Parent</InputLabel>
                  <Select
                    value={selectedParent}
                    onChange={(e) => setSelectedParent(e.target.value)}
                    label="Select Parent"
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.coolGray,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.flameOrange,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.flameOrange,
                      }
                    }}
                  >
                    {parents.map((parent) => (
                      <MenuItem key={parent.id} value={parent.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ 
                            bgcolor: safeLineColors.flameOrange,
                            mr: 2,
                            width: 32,
                            height: 32,
                            fontSize: '0.8rem'
                          }}>
                            {getInitials(parent.name)}
                          </Avatar>
                          <Box>
                            <Typography variant="body1">{parent.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {parent.email}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tag Number"
                  placeholder="Enter tag number (e.g., TAG004)"
                  value={tagNumber}
                  onChange={(e) => setTagNumber(e.target.value.toUpperCase())}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: safeLineColors.coolGray,
                      },
                      '&:hover fieldset': {
                        borderColor: safeLineColors.flameOrange,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: safeLineColors.flameOrange,
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-focused': {
                        color: safeLineColors.flameOrange
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button 
              onClick={() => setAssignDialogOpen(false)}
              sx={{ 
                borderRadius: 2,
                color: safeLineColors.mediumCharcoal,
                '&:hover': {
                  backgroundColor: safeLineColors.lightGray
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAssignTag}
              variant="contained"
              sx={{
                borderRadius: 2,
                background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${safeLineColors.brightOrange}, ${safeLineColors.flameOrange})`
                }
              }}
            >
              Assign Tag
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default TaggingIdentifier; 